import { AccessRole } from "@/types/auth";
import { apiClient } from "@/lib/apiClient";
import type { ApiResponse } from "@/lib/apiClient";

export interface SendOTPRequest {
  phone: string;
  inviteId?: string;
  channel?: string;
}

export interface SendOTPResponse {
  authenticationId: string;
  message: string;
  valid: boolean;
}

export interface VerifyOTPRequest {
  authenticationId: string;
  otp: string;
}

export interface VerifyOTPResponse {
  message: string;
  valid: boolean;
  newUser: boolean;
  newStore?: string; // UUID string or null
  userId: string;
  stores: AccessRole[]; // List of stores user has access to
  adminCookie?: string; // For debugging
}

export interface AuthError {
  message: string;
  code?: string;
}

// StatusResponse now matches verifyOTPAdminLoginResponse format
export interface StatusResponse {
  message: string;
  valid: boolean;
  newUser: boolean;
  newStore?: string; // UUID string or null
  userId: string;
  phoneNumber?: string;
  stores: AccessRole[]; // List of stores user has access to
  adminCookie?: string; // For debugging
}

export async function sendOTP(
  phone: string,
  inviteId?: string,
  channel?: string,
): Promise<SendOTPResponse> {
  try {
    const requestBody: SendOTPRequest = { phone };
    if (inviteId) {
      requestBody.inviteId = inviteId;
    }
    if (channel) {
      requestBody.channel = channel;
    }

    const response = await apiClient.post<SendOTPResponse>(
      "/api/v1/auth/admin/send-otp",
      requestBody,
    );

    if (!response.valid) {
      throw new Error(response.message || "Failed to send OTP");
    }

    return {
      authenticationId: response.data.authenticationId,
      message: response.message,
      valid: response.valid,
    };
  } catch (error) {
    console.error("Send OTP error:", error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("Network error occurred while sending OTP");
  }
}

export async function verifyOTP(
  authenticationId: string,
  otp: string,
): Promise<VerifyOTPResponse> {
  try {
    const response = await apiClient.post<VerifyOTPResponse>(
      "/api/v1/auth/admin/verify-otp",
      {
        authenticationId,
        otp,
      },
    );

    if (!response.valid) {
      throw new Error(response.message || "Invalid OTP");
    }

    return {
      message: response.message,
      valid: response.valid,
      newUser: response.data.newUser,
      newStore: response.data.newStore,
      userId: response.data.userId,
      stores: response.data.stores || [],
      adminCookie: response.data.adminCookie,
    };
  } catch (error) {
    console.error("Verify OTP error:", error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("Network error occurred while verifying OTP");
  }
}

export async function getAuthStatus(): Promise<StatusResponse> {
  try {
    const response = await apiClient.getAxiosInstance().get<
      ApiResponse<StatusResponse>
    >("/api/v1/auth/admin/status", {
      params: {
        _ts: Date.now(),
      },
    });

    const data = response.data?.data || ({} as StatusResponse);

    return {
      message: response.data?.message || "",
      valid: response.data?.valid ?? false,
      newUser: data.newUser || false,
      newStore: data.newStore || "",
      userId: data.userId || "",
      phoneNumber: data.phoneNumber,
      stores: data.stores || [],
      adminCookie: data.adminCookie,
    };
  } catch (error) {
    console.error("Auth status error:", error);

    // Return default unauthorized status on error
    return {
      message: "Error checking authorization status",
      valid: false,
      newUser: false,
      userId: "",
      phoneNumber: undefined,
      stores: [],
    };
  }
}

export interface LoginToStoreRequest {
  storeId: string;
}

export interface LoginToStoreResponse {
  message: string;
  valid: boolean;
  newUser: boolean;
  newStore: string | null;
  userId: string;
  stores: AccessRole[];
}

export async function loginToStore(
  storeId: string,
): Promise<LoginToStoreResponse> {
  try {
    const response = await apiClient.post<LoginToStoreResponse>(
      "/api/v1/auth/admin/login-to-store",
      { storeId },
    );

    console.log("[authService] loginToStore raw response:", response);

    if (!response.valid) {
      throw new Error(response.message || "Failed to login to store");
    }

    // The API returns the same format as status API
    return {
      message: response.message,
      valid: response.valid,
      newUser: response.data?.newUser || false,
      newStore: response.data?.newStore || null,
      userId: response.data?.userId || "",
      stores: response.data?.stores || [],
    };
  } catch (error) {
    console.error("Login to store error:", error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("Network error occurred while logging into store");
  }
}

export interface CreateNewStoreResponse {
  // Old snake_case shapes
  seller_store?: any;
  // API may return PascalCase keys
  SellerStore?: any;
  // fallback direct store object
  store?: any;
}

export interface CreateNewStoreRequest {
  store_type: "seller";
  personal_name: string;
  personal_email: string;
  personal_phone_number: string;
  business_name: string;
  business_email: string;
  business_phone_number: string;
  address_line_one: string;
  address_line_two?: string;
  city: string;
  state: string;
  postal_code: string;
}

export async function createNewStore(
  payload: CreateNewStoreRequest,
): Promise<CreateNewStoreResponse> {
  try {
    const response = await apiClient.post<CreateNewStoreResponse>(
      "/api/v1/auth/admin/new-store",
      payload,
    );

    if (!response.valid) {
      throw new Error(response.message || "Failed to create store");
    }

    // return the raw data payload for callers to extract created store
    return response.data as CreateNewStoreResponse;
  } catch (error) {
    console.error("Create new store error:", error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Network error occurred while creating store");
  }
}

/**
 * Logout user by clearing session
 * Note: This will be implemented when we have the logout endpoint
 */
// export async function logout(): Promise<void> {
//   try {
//     // For now, we'll just clear local storage and cookies will be handled by server
//     // When logout endpoint is available, we'll call it here
//     localStorage.removeItem("authToken");
//   } catch (error) {
//     console.error("Logout error:", error);
//   }
// }

/**
 * Check if user is authenticated by checking for session cookie
 * This is a client-side check - server-side verification should be done separately
 */
// export function isAuthenticated(): boolean {
//   // This is a basic check - in production, you might want to verify the session with the server
//   if (typeof window === "undefined") return false;

//   // Check if session cookie exists (basic check)
//   return (
//     document.cookie.includes("session_id=") ||
//     document.cookie.includes("session=")
//   );
// }
